if (!customElements.get('upload-button')) {
  customElements.define(
    'upload-button',
    class UploadButton extends HTMLElement {
      static CANVAS_SUPPORTED_FILE_SIZE = 10 * 1024 * 1024;
      static MAX_SUPPORTED_TRANSFORM_FILE_SIZE = 50 * 1024 * 1024;
      static MAX_SUPPORTED_FILE_SIZE = 250 * 1024 * 1024;
      static ONLY_RESIZE_SUPPORTED_FORMATS = ['application/pdf'];
      // Files we upload as-is: no canvas mockup, just the file name in a card.
      static NO_PREVIEW_FILE_TYPES = ['application/pdf', 'application/postscript', 'application/illustrator'];
      static NO_PREVIEW_FILE_EXTENSIONS = ['pdf', 'ai'];
      // Windows reports no type at all for these, and the upload is signed against the content type.
      static FALLBACK_CONTENT_TYPES = { pdf: 'application/pdf', ai: 'application/illustrator' };
      static RESIZE_SUPPORTED_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/webp', 'image/png', '.ico', 'image/HEIC'];
      static CANVAS_SUPPORTED_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/webp', 'image/png', 'image/svg+xml'];
      static SUPABASE_URL = 'https://mgettdoycdkgplgaknqd.supabase.co';
      static API_BASE_URL = 'https://api.rugtomize.co';
      static UPLOAD_LATER_PROPERTY = 'Order Now Upload Later';
      static UPLOAD_LATER_VALUE = 'Reply to your order confirmation email to send your logo!';

      constructor() {
        super();
        this.selectedFile = null;
        this.onClickListener = this.onClick.bind(this);
        this.onFileChangeListener = this.onFileChange.bind(this);
        this.onColorChangeListener = this.onColorChange.bind(this);
        this.onRemoveBgListener = this.onRemoveBg.bind(this);
        this.onAddToCartListener = this.onAddToCart.bind(this);
        this.onEditNotesListener = this.onEditNotes.bind(this);
        this.onSaveNotesListener = this.onSaveNotes.bind(this);
        this.onFormSubmitListener = this.onFormSubmit.bind(this);
        this.onUploadLaterListener = this.onUploadLater.bind(this);
        this.onPageShowListener = this.onPageShow.bind(this);
      }

      get imageInput() {
        return document.querySelector('#custom-rug-image');
      }

      get bgColorInput() {
        return document.querySelector('[data-bg-color="color"]');
      }

      get removeBgInput() {
        return document.querySelector('#custom-rug-remove-bg');
      }

      get addToCartButton() {
        return document.querySelector('#custom-rug-add-to-cart');
      }

      get addToCartButtonText() {
        return document.querySelector('#custom-rug-add-to-cart .btn-text span');
      }

      get editNotesButton() {
        return document.querySelector('.custom-rug-edit-notes');
      }

      get saveNotesButton() {
        return document.querySelector('.edit-note-save-button');
      }

      get editNoteHeaderButtons() {
        return document.querySelectorAll('.edit-notes-header-button');
      }

      get productForm() {
        const formId = this.getAttribute('data-form-id');
        return formId ? document.getElementById(formId) : null;
      }

      get uploadLaterButton() {
        return document.querySelector('#custom-rug-upload-later');
      }

      // The buy buttons render their own form id, so fall back to it when this
      // element was rendered without a data-form-id.
      get cartForm() {
        return this.productForm || document.getElementById('product-form-custom');
      }

      connectedCallback() {
        this.addEventListener('click', this.onClickListener);
        this.imageInput?.addEventListener('change', this.onFileChangeListener);
        this.bgColorInput?.addEventListener('input', this.onColorChangeListener);
        this.removeBgInput?.addEventListener('click', this.onRemoveBgListener);
        this.addToCartButton?.addEventListener('click', this.onAddToCartListener);
        this.editNotesButton?.addEventListener('click', this.onEditNotesListener);
        this.saveNotesButton?.addEventListener('click', this.onSaveNotesListener);
        this.editNoteHeaderButtons.forEach((btn) => btn.addEventListener('click', this.onEditNoteHeader.bind(this)));
        this.productForm?.addEventListener('submit', this.onFormSubmitListener);
        // Only the non-sticky instance binds it, otherwise the single button
        // would get two listeners and add the item to cart twice.
        if (!this.hasAttribute('data-sticky')) {
          this.uploadLaterButton?.addEventListener('click', this.onUploadLaterListener);
          window.addEventListener('pageshow', this.onPageShowListener);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClickListener);
        this.imageInput?.removeEventListener('change', this.onFileChangeListener);
        this.bgColorInput?.removeEventListener('input', this.onColorChangeListener);
        this.removeBgInput?.removeEventListener('click', this.onRemoveBgListener);
        this.addToCartButton?.removeEventListener('click', this.onAddToCartListener);
        this.editNotesButton?.removeEventListener('click', this.onEditNotesListener);
        this.saveNotesButton?.removeEventListener('click', this.onSaveNotesListener);
        this.productForm?.removeEventListener('submit', this.onFormSubmitListener);
        this.uploadLaterButton?.removeEventListener('click', this.onUploadLaterListener);
        window.removeEventListener('pageshow', this.onPageShowListener);
      }

      // Coming back from checkout restores the page as it was left, so the
      // in-flight flag has to be cleared or the button stays dead.
      onPageShow(event) {
        if (!event.persisted) return;
        const button = this.uploadLaterButton;
        if (button) button.dataset.busy = 'false';
      }

      onClick(event) {
        // imageInput lives inside this element, so imageInput.click() below re-enters
        // this handler when the synthetic click bubbles back up. Without this guard the
        // CTA reports twice for every real click.
        if (event?.target === this.imageInput) return;

        theme.utils.track('Upload CTA', {
          cta_location: this.hasAttribute('data-sticky') ? 'sticky_bar' : 'product_form'
        });
        this.imageInput?.click();
      }

      static extensionOf(file) {
        return file.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
      }

      // Some browsers report an empty type for PDF and AI files, so fall back to the extension.
      static hasPreview(file) {
        return !UploadButton.NO_PREVIEW_FILE_TYPES.includes(file.type)
          && !UploadButton.NO_PREVIEW_FILE_EXTENSIONS.includes(UploadButton.extensionOf(file));
      }

      static contentTypeFor(file) {
        return file.type || UploadButton.FALLBACK_CONTENT_TYPES[UploadButton.extensionOf(file)] || 'application/octet-stream';
      }

      async onFileChange(event) {
        const input = event.target;
        const file = input.files?.[0];
        this.selectedFile = file;
        if (!file) return;

        const fileType = UploadButton.contentTypeFor(file);
        const fileSizeKb = Math.round(file.size / 1024);

        theme.utils.track('File Upload', {
          file_type: fileType,
          file_size_kb: fileSizeKb,
          has_preview: UploadButton.hasPreview(file)
        });
        theme.utils.tagClarity({ fileName: file.name });

        try {
          if (file.size > UploadButton.MAX_SUPPORTED_FILE_SIZE) {
            theme.utils.track('Upload Failed', {
              file_type: fileType,
              file_size_kb: fileSizeKb,
              error_stage: 'file_too_large'
            });
            alert('Maximum supported file size is 250MB.');
            return;
          }

          CanvasRugEditor.reset();

          if (!UploadButton.hasPreview(file)) {
            CanvasRugEditor.showFilePreview(file);
            this.uploadInBackground(file, { useUploadedFile: true });
            return;
          }

          CanvasRugEditor.showCanvas();
          this.uploadInBackground(file, { useUploadedFile: true });
          this.scrollToCanvas();
        } finally {
          input.value = '';
        }
      }

      async onUploadLater(event) {
        event.preventDefault();
        event.stopPropagation();

        const button = this.uploadLaterButton;
        if (!button || button.dataset.busy === 'true') return;

        const form = this.cartForm;
        const variantId = form?.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        const quantity = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;

        button.dataset.busy = 'true';
        theme.utils.track('Order Now Upload Later', { variant_id: variantId, quantity: quantity });
        window.GlobalLoader?.show(['Adding to cart...', 'Taking you to checkout...']);

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: quantity,
                  properties: { [UploadButton.UPLOAD_LATER_PROPERTY]: UploadButton.UPLOAD_LATER_VALUE }
                }
              ]
            })
          });

          if (!res.ok) throw new Error('Failed to add to cart: ' + res.status);

          theme.utils.track('Order Now Upload Later Checkout');
          window.location.href = '/checkout';
        } catch (error) {
          console.error(error);
          theme.utils.track('Order Now Upload Later Failed', { error_stage: 'cart_add' });
          window.GlobalLoader?.hide();
          button.dataset.busy = 'false';
        }
      }

      onRemoveBg() {
        const shownImage = CanvasRugEditor.startRemoveBg();
        if (shownImage === 'main') {
          this.removeBgInput.style.backgroundColor = 'white';
          this.removeBgInput.style.color = 'black';
        } else {
          this.removeBgInput.style.backgroundColor = '#272726';
          this.removeBgInput.style.color = 'white';
        }
      }

      onColorChange(event) {
        event.stopPropagation();
        event.preventDefault();
        CanvasRugEditor.setBgColor(event.target.value);
      }

      async onAddToCart() {
        try {
          theme.utils.track('Add to cart [Custom]');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          this.addToCartButton.disabled = true;
          CanvasRugEditor.loading(true, true, 'Preparing your rug...');
          await CanvasRugEditor.handleClickAddToCart();
          CanvasRugEditor.loading(true, true, 'Adding to cart...');
          setTimeout(() => { 
            this.addToCartButton.disabled = false;
            CanvasRugEditor.loading(false, true);
          }, 3000);
        } catch (error) {
          console.error(error);
          CanvasRugEditor.loading(false, false);
          this.addToCartButton.disabled = false;
          alert('Something went wrong adding your design to the cart. Please try again or contact support.');
        }
      }

      onEditNotes() {
        document.querySelector('#product-main-form-container').style.transform = 'translateX(calc(-100%))';
        document.querySelector('.edit-notes-section-container').style.display = 'block';
      }

      onSaveNotes() {
        document.querySelector('#product-main-form-container').style.transform = 'unset';
        document.querySelector('.edit-notes-section-container').style.display = 'none';
      }

      onEditNoteHeader(event) {
        const btn = event.currentTarget;
        const value = btn.dataset.value;

        if (value === 'needs-fixing') {
          btn.setAttribute('data-active', 'true');
          document.querySelector('.edit-notes-header-button[data-value="keep-mockup"]')?.removeAttribute('data-active');
          document.querySelector('.edit-notes-textarea').style.display = 'block';
        } else if (value === 'keep-mockup') {
          btn.setAttribute('data-active', 'true');
          document.querySelector('.edit-notes-header-button[data-value="needs-fixing"]')?.removeAttribute('data-active');
          document.querySelector('.edit-notes-textarea').style.display = 'none';
        }
      }

      async onFormSubmit(event) {
        event.preventDefault();

        const btn = this.addToCartButton;
        const btnText = this.addToCartButtonText;
        const formData = new FormData(this.productForm);

        CanvasRugEditor.loading(true, true, 'Adding to cart...');

        try {
          const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
          await res.json();

          const cart = await (await fetch('/cart.js')).json();

          setTimeout(() => {
            refreshCart(cart);
            setTimeout(() => {
              btn.disabled = false;
              btnText.textContent = 'Add to cart';
              CanvasRugEditor.loading(false, true);
              CanvasRugEditor.afterAddToCart();
            }, 500);
          }, 500);
        } catch (error) {
          console.error(error);
          btn.disabled = false;
          btnText.textContent = 'Add to cart';
          CanvasRugEditor.loading(false, true);
        }
      }

      scrollToCanvas() {
        const canvas = document.getElementById('rug-canvas-container');
        if (canvas) {
          window.scrollTo({ top: canvas.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
        }
      }

      svgFileToPngBlob(file, options = {}) {
        const { width = 400, height = 400, background = null, scale = 1 } = options;

        return new Promise(async (resolve, reject) => {
          try {
            if (!file || file.type !== 'image/svg+xml') return reject(new Error('Input must be an SVG file'));

            const svgText = await file.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgText, 'image/svg+xml');
            const svg = doc.documentElement;

            const svgWidth = svg.getAttribute('width') || svg.viewBox?.baseVal?.width || width;
            const svgHeight = svg.getAttribute('height') || svg.viewBox?.baseVal?.height || height;
            svg.setAttribute('width', svgWidth);
            svg.setAttribute('height', svgHeight);

            const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' }));
            const img = new Image();

            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = svgWidth * scale;
              canvas.height = svgHeight * scale;
              const ctx = canvas.getContext('2d');
              ctx.scale(scale, scale);
              if (background) {
                ctx.fillStyle = background;
                ctx.fillRect(0, 0, svgWidth, svgHeight);
              }
              ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
              URL.revokeObjectURL(url);
              canvas.toBlob((pngBlob) => {
                pngBlob ? resolve(pngBlob) : reject(new Error('PNG conversion failed'));
              }, 'image/png');
            };

            img.onerror = () => {
              URL.revokeObjectURL(url);
              reject(new Error('Failed to load SVG into image'));
            };

            img.src = url;
          } catch (err) {
            reject(err);
          }
        });
      }

      async uploadInBackground(file, options = {}) {
        const useUploadedFile = !!options.useUploadedFile;
        const hasPreview = UploadButton.hasPreview(file);
        const contentType = UploadButton.contentTypeFor(file);
        const startedAt = Date.now();
        let stage = 'sign';
        let res;

        const addToCartBtn = this.addToCartButton;
        if (addToCartBtn) addToCartBtn.disabled = true;

        try {
          window.useUploadedFileFlag = useUploadedFile;
          if (useUploadedFile) CanvasRugEditor.loading(true, false, 'Uploading your design...');
          CanvasRugEditor.setCircularProgressStatic(0);

          res = await fetch(`${UploadButton.API_BASE_URL}/api/media/upload-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileName: file.name, fileSize: file.size, contentType })
          });

          if (!res.ok) throw new Error('Failed to get upload URL: ' + res.status);

          const { bucket, path, token } = await res.json();
          stage = 'transfer';

          const uploadUrl = `${UploadButton.SUPABASE_URL}/storage/v1/object/upload/sign/${encodeURIComponent(bucket)}/${encodeURIComponent(path)}?token=${encodeURIComponent(token)}`;

          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Content-Type', contentType);
            xhr.upload.onprogress = (evt) => {
              if (evt.lengthComputable) CanvasRugEditor.setCircularProgressStatic(Math.round((evt.loaded / evt.total) * 100));
            };
            xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject('Upload failed with status ' + xhr.status);
            xhr.onerror = () => reject('XHR Error');
            xhr.send(file);
          });

          stage = 'render';
          if (useUploadedFile) CanvasRugEditor.loading(true, false, hasPreview ? 'Creating your rug...' : 'Processing your file...');

          let blob = null;
          let originalUrl;

          try {
            if (file.type.toLowerCase().includes('svg')) {
              blob = await this.svgFileToPngBlob(file);
            } else if (
              !hasPreview ||
              UploadButton.ONLY_RESIZE_SUPPORTED_FORMATS.includes(file.type) ||
              (file.size > UploadButton.MAX_SUPPORTED_TRANSFORM_FILE_SIZE && UploadButton.RESIZE_SUPPORTED_FILE_TYPES.includes(file.type))
            ) {
              const resResize = await fetch(`${UploadButton.API_BASE_URL}/api/media/resize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path })
              });
              if (!resResize.ok) throw new Error('Resize failed');
              blob = await resResize.blob();
            } else {
              const signedUrlResponse = await fetch(`${UploadButton.API_BASE_URL}/api/media/preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path })
              });
              if (!signedUrlResponse.ok) throw new Error('Failed to get preview URL');
              ({ url: originalUrl } = await signedUrlResponse.json());
              const imageRes = await fetch(originalUrl);
              if (!imageRes.ok) throw new Error('Failed to download image from URL');
              blob = await imageRes.blob();
            }
          } catch (error) {
            // The file itself is uploaded; without a canvas the rendered preview is optional, so keep going.
            if (hasPreview) throw error;
            console.error(error);
          }

          if (useUploadedFile) {
            if (hasPreview) {
              CanvasRugEditor.loadForeground(URL.createObjectURL(blob));
            } else {
              CanvasRugEditor.setFilePreviewStatus('ready', 'Uploaded');
              CanvasRugEditor.loading(false);
            }
          }

          CanvasRugEditor.setUploadedImage({ path, token, bucket, fileName: file.name, mimeType: contentType, originalPreviewFileUrl: originalUrl, previewDataUrl: blob });

          theme.utils.track('Upload Completed', {
            file_type: contentType,
            file_size_kb: Math.round(file.size / 1024),
            has_preview: hasPreview,
            duration_ms: Date.now() - startedAt
          });
        } catch (error) {
          console.error(error);
          theme.utils.track('Upload Failed', {
            file_type: contentType,
            file_size_kb: Math.round(file.size / 1024),
            error_stage: stage,
            error_code: res?.status || 'exception',
            duration_ms: Date.now() - startedAt
          });
          if (res?.status === 429) alert('Maximum upload limit reached. Please contact support.');
          CanvasRugEditor.loading(false, false);
        } finally {
          if (addToCartBtn) addToCartBtn.disabled = false;
        }
      }
    }
  );
}
